/**
 * Reconnaît un scan venu du clavier émulé du service Sunmi, sans le couper.
 *
 * Retour terrain (V2s de Vallon, 2026-11-02) : avec un seuil strict de 50 ms entre touches,
 * des codes réels étaient tronqués (WebView occupée, rafale qui écrit en base) puis lus
 * « inconnus ». Deux modes :
 *  - « libre » (champ de scan, bouton, page : aucun vrai champ texte) : toutes les touches
 *    s'accumulent jusqu'à Entrée, quel que soit l'intervalle ; 1 s sans touche efface ;
 *  - « saisie » (vrai champ texte) : seule une rafale rapide (< 50 ms entre touches) est un
 *    scan ; une frappe humaine reste une frappe.
 * Moins de 6 caractères : jamais un code. Pur, testé.
 */
export const SCAN_MAX_GAP_MS = 50;
export const SCAN_IDLE_RESET_MS = 1000;
export const SCAN_MIN_LENGTH = 6;

export type ScanMode = 'free' | 'typing';

export type ScanDetector = {
  /** Touche reçue ; rend le code quand Entrée le termine, sinon null. */
  feed: (key: string, at: number, mode: ScanMode) => string | null;
  reset: () => void;
};

/** Touches sans caractère (Maj, verrouillage…) : ignorées sans couper le code. */
const isPrintable = (key: string): boolean => key.length === 1 && /^[\x21-\x7e]$/.test(key);

export { cleanScannedCode } from '@/lib/scanCode';

export const createScanDetector = (
  maxGapMs = SCAN_MAX_GAP_MS,
  minLength = SCAN_MIN_LENGTH,
  idleResetMs = SCAN_IDLE_RESET_MS,
): ScanDetector => {
  let buffer = '';
  let last = -Infinity;
  return {
    feed(key, at, mode) {
      const gap = at - last;
      if (key === 'Enter') {
        const fast = mode === 'free' ? gap <= idleResetMs : gap <= maxGapMs;
        const code = fast && buffer.length >= minLength ? buffer : null;
        buffer = '';
        last = at;
        return code;
      }
      if (!isPrintable(key)) return null;
      const keep = mode === 'free' ? gap <= idleResetMs : gap <= maxGapMs;
      buffer = keep ? buffer + key : key;
      last = at;
      return null;
    },
    reset() {
      buffer = '';
      last = -Infinity;
    },
  };
};
